import { useEffect, useMemo, useState } from 'react';
import { get, post } from '../api';
import { useData } from '../App';
import { fmtLong, fmtShort, GRID, parseIso, rangeLbl, slotLabel, SLOTS } from '../lib';
import type { MgBooking } from '../types';
import { IconInfo, IconWarn, Modal, useFail, useToast } from '../ui';

const REASONS = ['Férias', 'Desligamento', 'Ausência confirmada', 'Afastamento', 'Outro'];
const DOW = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const dm = (d: string) => d.slice(8, 10) + '/' + d.slice(5, 7);

/**
 * Recorrência (Gestão de reservas): cada data da série aparece sozinha na lista; aqui a gestão cancela só a data,
 * a data e as próximas ou toda a recorrência, ou modifica horário e sala. As datas em que a nova sala está ocupada
 * ficam como estão, e a tela mostra quais são.
 */
export function SeriesDialog({ bookingId, onClose, onDone }: { bookingId: number; onClose: () => void; onDone: (msg: string) => void }) {
  const { me, catalog, roomById } = useData();
  const fail = useFail(), toast = useToast();
  const [data, setData] = useState<{ base: MgBooking; items: MgBooking[] } | null>(null);
  const [mode, setMode] = useState<'cancel' | 'edit'>('cancel');
  const [scope, setScope] = useState<'one' | 'next' | 'all'>('next');
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [f, setF] = useState({ s: 0, e: 1, room: '' });
  const [preview, setPreview] = useState<{ id: number; ok: boolean }[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    get<{ base: MgBooking; items: MgBooking[] }>(`manage/series/${bookingId}`)
      .then(d => { setData(d); setF({ s: d.base.start, e: d.base.end, room: d.base.roomId }); }).catch(err => { fail(err); onClose(); });
  }, [bookingId]); // eslint-disable-line react-hooks/exhaustive-deps
  const editScope = scope === 'one' ? 'next' : scope;
  useEffect(() => {
    setPreview(null);
    if (!data || mode !== 'edit') return;
    post<{ items: { id: number; ok: boolean }[] }>('manage/series/edit', { bookingId, scope: editScope, start: f.s, end: f.e, roomId: f.room, preview: true })
      .then(r => setPreview(r.items)).catch(fail);
  }, [data, mode, editScope, f]); // eslint-disable-line react-hooks/exhaustive-deps

  const targets = useMemo(() => {
    if (!data) return [];
    const sc = mode === 'edit' ? editScope : scope;
    return sc === 'one' ? [data.base] : sc === 'next' ? data.items.filter(x => x.date >= data.base.date) : data.items;
  }, [data, mode, scope, editScope]);
  if (!data) return null;
  const { base, items } = data;
  const tk = new Set(targets.map(x => x.id)), okSet = new Set((preview ?? []).filter(x => x.ok).map(x => x.id));
  const bad = mode === 'edit' && preview ? targets.filter(x => !okSet.has(x.id)).length : 0;
  const nNext = items.filter(x => x.date >= base.date).length;
  const baseRoom = roomById(base.roomId);
  const scopeRooms = catalog.rooms.filter(r => me.manageRooms.includes(r.id) && r.category === baseRoom?.category);
  // Opções de 10 em 10 min (o valor atual entra mesmo fora da grade, como os horários das aulas)
  const slotOpts = (a: number, z: number, cur?: number) => Array.from({ length: z - a + 1 }, (_, k) => a + k).filter(i => i % GRID === 0 || i === cur).map(i => <option key={i} value={i}>{slotLabel(i)}</option>);
  const opt = (v: 'one' | 'next' | 'all', l: string) => <label><input type="radio" name="sdScope" checked={(mode === 'edit' ? editScope : scope) === v} onChange={() => setScope(v)} /> {l}</label>;

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = await post<{ cancelled: number }>('manage/series/cancel', { bookingId, scope, reason, note: note.trim(), notify: true });
      onDone(`${r.cancelled} reserva${r.cancelled > 1 ? 's canceladas' : ' cancelada'} da recorrência de ${base.who}. ${base.who} foi avisado(a).`);
    } catch (err) { fail(err); } finally { setBusy(false); }
  };
  const doEdit = async () => {
    setBusy(true);
    try {
      const r = await post<{ changed: number; kept: string[] }>('manage/series/edit', { bookingId, scope: editScope, start: f.s, end: f.e, roomId: f.room, notify: true });
      onDone(`Recorrência alterada: ${r.changed} data${r.changed > 1 ? 's' : ''}${r.kept.length ? `; ${r.kept.length} ${r.kept.length > 1 ? 'ficaram como estavam' : 'ficou como estava'} porque a sala está ocupada` : ''}. ${base.who} foi avisado(a).`);
    } catch (err) { fail(err); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} className="dlg sm" labelledBy="sdTitle">
      <div className="wrap">
        <div className="dlg-h"><h3 id="sdTitle">Recorrência de {base.who}</h3>
          <p>{baseRoom?.name} · toda {DOW[parseIso(base.date).getDay()]}, <span className="num">{rangeLbl(base.start, base.end - 1)}</span> · {items.length} datas a partir de hoje</p></div>
        <div className="dlg-b">
          <div className="seg" role="group" aria-label="Ação">
            <button type="button" aria-pressed={mode === 'cancel'} onClick={() => setMode('cancel')}>Cancelar recorrência</button>
            <button type="button" aria-pressed={mode === 'edit'} onClick={() => setMode('edit')}>Modificar recorrência</button>
          </div>
          <div className="sdlist" aria-label="Datas da recorrência">
            {items.map(x => <span key={x.id} title={fmtLong(x.date)} className={tk.has(x.id) ? (mode === 'edit' && preview && !okSet.has(x.id) ? 'bad' : 'on') : ''}>{dm(x.date)}</span>)}
          </div>
          {mode === 'cancel' ? <>
            <div className="field"><span className="lbl">O que cancelar</span><div className="opts">
              {opt('one', `Só esta data (${fmtShort(base.date)})`)}{opt('next', `Esta e as próximas (${nNext} datas)`)}{opt('all', `Toda a recorrência a partir de hoje (${items.length} datas)`)}</div></div>
            <div className="field"><label htmlFor="sdReason">Motivo</label><select id="sdReason" value={reason} onChange={e => setReason(e.target.value)}>{REASONS.map(r => <option key={r}>{r}</option>)}</select></div>
            <div className="field"><label htmlFor="sdNote">Observação (opcional)</label><textarea id="sdNote" value={note} maxLength={300} onChange={e => setNote(e.target.value)} /></div>
            <div className="note warn"><IconWarn /><span><b>{targets.length} reserva{targets.length > 1 ? 's' : ''}</b> {targets.length > 1 ? 'serão canceladas' : 'será cancelada'}. As salas ficam livres e {base.who} recebe o aviso no Teams.</span></div>
          </> : <>
            <div className="field"><span className="lbl">Quais datas mudar</span><div className="opts">
              {opt('next', `Esta e as próximas (${nNext} datas)`)}{opt('all', `Toda a recorrência a partir de hoje (${items.length} datas)`)}</div>
              <span className="hint">Para mudar só uma data, use "Editar esta data" na lista.</span></div>
            <div className="row">
              <div className="field"><label htmlFor="sdS">Início</label><select id="sdS" value={f.s} onChange={e => { const s = +e.target.value; setF({ ...f, s, e: f.e <= s ? s + GRID : f.e }); }}>{slotOpts(0, SLOTS - 1, f.s)}</select></div>
              <div className="field"><label htmlFor="sdE">Término</label><select id="sdE" value={f.e} onChange={e => setF({ ...f, e: +e.target.value })}>{slotOpts(f.s + 1, SLOTS, f.e)}</select></div>
            </div>
            <div className="field"><label htmlFor="sdRoom">Sala</label><select id="sdRoom" value={f.room} onChange={e => setF({ ...f, room: e.target.value })}>{scopeRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
            {!preview ? <span className="hint">Conferindo as datas…</span> : bad
              ? <div className="note warn"><IconWarn /><span><b>{bad} data{bad > 1 ? 's' : ''}</b> ({bad > 1 ? 'riscadas' : 'riscada'} acima) {bad > 1 ? 'ficam como estão' : 'fica como está'}, porque a sala está ocupada no novo horário. As outras {okSet.size} mudam.</span></div>
              : <div className="note"><IconInfo /><span>As {okSet.size} datas mudam para {roomById(f.room)?.name}, {rangeLbl(f.s, f.e - 1)}. {base.who} recebe o aviso no Teams.</span></div>}
          </>}
        </div>
        <div className="dlg-f"><button type="button" className="btn ghost" onClick={onClose}>Voltar</button>
          {mode === 'cancel'
            ? <button type="button" className="btn danger" disabled={busy} onClick={doCancel}>Cancelar {targets.length} reserva{targets.length > 1 ? 's' : ''}</button>
            : <button type="button" className="btn" disabled={busy || !preview || !okSet.size} onClick={() => { if (!okSet.size) { toast('Nenhuma data pode mudar para esse horário e sala.'); return; } doEdit(); }}>Salvar {okSet.size} data{okSet.size === 1 ? '' : 's'}</button>}</div>
      </div>
    </Modal>
  );
}
